import { useCallback, useEffect, useState } from 'react'
import { useI18n } from '../../i18n'
import { API_ENDPOINTS } from '../../config/api'
import { featureFlags } from '../../config/featureFlags'
import { useAuth } from '../AuthProvider'
import { Button } from '../ui/Button'
import entStyles from '../../styles/enterprise/EnterpriseOperationalUx.module.scss'
import { EnterpriseOperationalCard } from '../enterprise-operational/EnterpriseOperationalSection'
import OpenAiProviderIntegrationCard from './OpenAiProviderIntegrationCard'
import OrgByokProviderCard from './OrgByokProviderCard'
import PlatformAiProvidersPanel from './PlatformAiProvidersPanel'

type ProviderRow = {
  id: string
  providerKey: string
  displayName: string
  providerType: string
  enabled: boolean
  healthStatus?: string
  apiKeyMasked?: string | null
  hasApiKey?: boolean
  baseUrl?: string | null
  model?: string | null
  embeddingModel?: string | null
  lastTestedAt?: string | null
  defaultForChat?: boolean
  defaultForEmbeddings?: boolean
  defaultForRag?: boolean
  ownership?: string
}

type PolicyTier = {
  tier: string
  externalLlmAllowed: boolean
  localLlmAllowed: boolean
  embeddingsAllowed: boolean
  metadataExtractionAllowed: boolean
  redactionRequired: boolean
  bodyTransmissionAllowed: boolean
}

type EffectiveState = {
  available?: boolean
  ownership?: string | null
  providerKey?: string | null
  modelKey?: string | null
  reason?: string | null
  code?: string
}

type Props = {
  token: string | null
}

export default function AiIntegrationsPhase3aPanel({ token }: Props) {
  const { t } = useI18n()
  const { user } = useAuth() as { user?: { role?: string; permissions?: string[] } }
  const role = String(user?.role || '').toLowerCase()
  const perms = Array.isArray(user?.permissions) ? user.permissions : []
  const isPlatformAdmin = role === 'admin' || perms.includes('admin') || perms.includes('all')

  const [runtimeMode, setRuntimeMode] = useState('UNCONFIGURED')
  const [providers, setProviders] = useState<ProviderRow[]>([])
  const [policyTiers, setPolicyTiers] = useState<PolicyTier[]>([])
  const [effective, setEffective] = useState<EffectiveState | null>(null)
  const [externalAllowed, setExternalAllowed] = useState<boolean | null>(null)
  const [localOnlyMode, setLocalOnlyMode] = useState(false)
  const [testMessage, setTestMessage] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const headers = token ? { Authorization: `Bearer ${token}` } : undefined

  const load = useCallback(async () => {
    if (!token) return
    setLoading(true)
    try {
      const res = await fetch(API_ENDPOINTS.AI_PROVIDERS_V2, { headers })
      if (res.ok) {
        const json = await res.json().catch(() => ({}))
        setProviders(Array.isArray(json?.data?.providers) ? json.data.providers : [])
        setRuntimeMode(String(json?.data?.runtimeMode || 'CONFIGURED'))
      } else if (res.status === 403) {
        setProviders([])
        setRuntimeMode('UNAUTHORIZED')
      }

      const effRes = await fetch(API_ENDPOINTS.AI_PROVIDERS_EFFECTIVE, { headers })
      if (effRes.ok) {
        const json = await effRes.json().catch(() => ({}))
        setEffective(json?.data?.effective || null)
        setExternalAllowed(Boolean(json?.data?.externalProcessingAllowed))
        setLocalOnlyMode(Boolean(json?.data?.localOnlyMode))
      }

      if (featureFlags.AI_DATA_POLICY_V1) {
        const policyRes = await fetch(API_ENDPOINTS.AI_DATA_POLICY, { headers })
        if (policyRes.ok) {
          const json = await policyRes.json().catch(() => ({}))
          setPolicyTiers(Array.isArray(json?.data?.tiers) ? json.data.tiers : [])
        }
      }
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => {
    void load()
  }, [load])

  const tt = (key: string, fallback: string) => t(key, fallback)

  const testProvider = async (id: string) => {
    setTestMessage(null)
    const res = await fetch(API_ENDPOINTS.AI_PROVIDER_TEST(id), {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: '{}',
    })
    const json = await res.json().catch(() => ({}))
    setTestMessage(json?.data?.message || json?.message || (res.ok ? 'Test complete' : 'Test failed'))
    void load()
  }

  return (
    <section className={entStyles.layer} data-testid="integrations-ai-phase3a">
      <p className={entStyles.layerLabel}>
        {t('integrations.ai.orgProviders', 'Organization AI Providers')}
      </p>
      <div className={entStyles.metaRow}>
        <span>
          {t('integrations.ai.runtimeMode', 'Runtime mode')}: {runtimeMode}
        </span>
        <span>
          {t('integrations.ai.externalProcessing', 'External processing')}:{' '}
          {externalAllowed == null ? '—' : externalAllowed ? 'Allowed' : 'Blocked'}
        </span>
        <span>LOCAL_ONLY: {localOnlyMode ? 'Yes' : 'No'}</span>
        <span>
          {t('integrations.ai.userManaged', 'User-managed keys')}:{' '}
          {t('integrations.ai.notSupported', 'Not supported')}
        </span>
        {loading ? <span>Loading…</span> : null}
      </div>

      {effective ? (
        <EnterpriseOperationalCard title={t('integrations.ai.effectiveProvider', 'Effective provider')}>
          <p className={entStyles.metaRow}>
            <span>{effective.available ? 'Available' : 'Unavailable'}</span>
            <span>{effective.ownership || '—'}</span>
            <span>{effective.providerKey || effective.reason || effective.code || '—'}</span>
            <span>{effective.modelKey || ''}</span>
          </p>
        </EnterpriseOperationalCard>
      ) : null}

      <OpenAiProviderIntegrationCard
        token={token}
        provider={providers.find((p) => p.providerKey === 'openai') || null}
        onSaved={() => void load()}
      />

      <div style={{ marginTop: 12 }}>
        <OrgByokProviderCard
          token={token}
          providerKey="xai_grok"
          title={t('integrations.ai.xaiGrok', 'xAI Grok (organization BYOK)')}
          defaultBaseUrl="https://api.x.ai/v1"
          defaultModel="grok-2-latest"
          provider={providers.find((p) => p.providerKey === 'xai_grok') || null}
          onSaved={() => void load()}
          t={tt}
        />
      </div>

      <div className={entStyles.standardGrid} style={{ marginTop: 12 }}>
        {providers
          .filter((p) => p.providerKey !== 'openai' && p.providerKey !== 'xai_grok')
          .map((provider) => (
            <EnterpriseOperationalCard key={provider.id} title={provider.displayName}>
              <p className={entStyles.metaRow}>
                <span>{provider.providerKey}</span>
                <span>{provider.ownership || 'ORGANIZATION_MANAGED'}</span>
                <span>{provider.enabled ? 'Enabled' : 'Disabled'}</span>
              </p>
              <p className={entStyles.metaRow}>
                <span>Health: {provider.healthStatus || 'unknown'}</span>
                <span>Key: {provider.apiKeyMasked || (provider.hasApiKey ? '****' : 'none')}</span>
              </p>
              <Button type="button" size="small" color="outline" onClick={() => void testProvider(provider.id)}>
                {t('integrations.ai.testConnection', 'Test connection')}
              </Button>
            </EnterpriseOperationalCard>
          ))}
      </div>

      {testMessage ? (
        <p className={entStyles.metaRow} style={{ marginTop: 8 }}>
          Last test: {testMessage}
        </p>
      ) : null}

      {featureFlags.AI_DATA_POLICY_V1 ? (
        <div className={entStyles.standardGrid} style={{ marginTop: 12 }}>
          {policyTiers.map((tier) => (
            <EnterpriseOperationalCard key={tier.tier} title={tier.tier}>
              <p className={entStyles.metaRow}>
                <span>External LLM: {tier.externalLlmAllowed ? 'Yes' : 'No'}</span>
                <span>Local LLM: {tier.localLlmAllowed ? 'Yes' : 'No'}</span>
              </p>
              <p className={entStyles.metaRow}>
                <span>Embeddings: {tier.embeddingsAllowed ? 'Yes' : 'No'}</span>
                <span>Metadata: {tier.metadataExtractionAllowed ? 'Yes' : 'No'}</span>
              </p>
              <p className={entStyles.metaRow}>
                <span>Redaction: {tier.redactionRequired ? 'Required' : 'Optional'}</span>
                <span>Body transmit: {tier.bodyTransmissionAllowed ? 'Allowed' : 'Blocked'}</span>
              </p>
            </EnterpriseOperationalCard>
          ))}
        </div>
      ) : null}

      <div style={{ marginTop: 16 }}>
        <PlatformAiProvidersPanel token={token} isPlatformAdmin={isPlatformAdmin} t={tt} />
      </div>
    </section>
  )
}
